import { useState, useEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { apiService } from '@/services/api'
import SupplierProfileSummary, { type SupplierProfile } from '@/features/proveedores/components/SupplierProfileSummary'
import ConfirmActionModal from '@/shared/components/ConfirmActionModal'
import Toast from '@/shared/components/Toast'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import type { PlazoDelExpediente as PlazoDelExpedienteDatos } from '@/features/proveedores/lib/plazoDelExpediente'

interface SupplierDetailResponse extends SupplierProfile {
  general: SupplierProfile['general'] & {
    origin: string
  }
  primaryBankAccount: SupplierProfile['primaryBankAccount'] & {
    supplierId: string
    isPrimary: boolean
  } | null
  documents: Array<{
    documentType: SupplierProfile['documents'][0]['documentType'] & {
      personType: string
    }
    latestDocument: SupplierProfile['documents'][0]['latestDocument'] & {
      supplierId: string
      documentTypeId: string
      version: number
      validFrom: string | null
      validUntil: string | null
      reviewedAt: string | null
      reviewerUserId: string | null
      createdAt: string
      updatedAt: string
    } | null
  }>
  hasAllDocumentsApproved?: boolean
  /** Plazo para completar el expediente (SP-247). Null/ausente: no aplica. */
  profileDeadline?: PlazoDelExpedienteDatos | null
}

interface SupplierRequestDetailProps {
  supplierId: string
  onBack: () => void
  onEdit?: () => void
}

const SupplierRequestDetail = ({ supplierId, onBack, onEdit }: SupplierRequestDetailProps) => {
  const [supplierData, setSupplierData] = useState<SupplierDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showApproveModal, setShowApproveModal] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const fetchSupplierDetail = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true)
      const response = await apiService.request<SupplierDetailResponse>(
        `/internal/suppliers/${supplierId}`
      )

      // Load documents from dedicated endpoint (same as EditSupplierForm)
      try {
        const docs = await apiService.request<Array<{
          id: string
          documentTypeId: string
          fileUrl: string
          fileName: string
          version: number
          status: string
          uploadedAt: string
          reviewComment: string | null
          hasComments: boolean
          commentsCount: number
          // El endpoint si devuelve la vigencia; el tipo no la declaraba, asi que
          // nadie se dio cuenta de que la pantalla la estaba tirando a la basura.
          validFrom: string | null
          validUntil: string | null
          estadoVigencia: 'vigente' | 'por_vencer' | 'vencido' | 'sin_fecha' | null
          reviewedAt: string | null
          reviewerUserId: string | null
          createdAt: string
          updatedAt: string
          documentType: { id: string; name: string; description: string; isRequired: boolean }
        }>>(`/internal/suppliers/${supplierId}/documents`)

        if (Array.isArray(docs) && docs.length > 0) {
          // Group by documentTypeId and keep only the highest version
          const latestByType = new Map<string, typeof docs[0]>()
          for (const doc of docs) {
            const current = latestByType.get(doc.documentTypeId)
            if (!current || doc.version > current.version) {
              latestByType.set(doc.documentTypeId, doc)
            }
          }

          response.documents = Array.from(latestByType.values()).map(d => ({
            documentType: {
              id: d.documentType.id,
              name: d.documentType.name,
              description: d.documentType.description,
              isRequired: d.documentType.isRequired,
              personType: ''
            },
            latestDocument: {
              id: d.id,
              fileName: d.fileName,
              fileUrl: d.fileUrl,
              status: d.status,
              uploadedAt: d.uploadedAt,
              reviewComment: d.reviewComment,
              hasComments: d.hasComments,
              commentsCount: d.commentsCount,
              supplierId: supplierId,
              documentTypeId: d.documentTypeId,
              version: d.version,
              // Estos venian fijos en null: la columna Fecha vencimiento decia
              // N/A siempre y el badge de vigencia no podia pintar, aunque el
              // endpoint si los devuelve. Es la pantalla que abre Compras, o sea
              // justo donde tiene que verse.
              validFrom: d.validFrom ?? null,
              validUntil: d.validUntil ?? null,
              estadoVigencia: d.estadoVigencia ?? undefined,
              reviewedAt: d.reviewedAt ?? null,
              reviewerUserId: d.reviewerUserId ?? null,
              createdAt: d.createdAt ?? d.uploadedAt,
              updatedAt: d.updatedAt ?? d.uploadedAt
            }
          }))
        }
      } catch {
        // If documents endpoint fails, keep whatever came from the main endpoint
      }

      setSupplierData(response)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar detalle del proveedor:', err)
      if (!supplierData) {
        setError(err.message || 'Error al cargar la información del proveedor')
      }
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)
    const mainContainer = document.querySelector('main')
    if (mainContainer) {
      mainContainer.scrollTo(0, 0)
    }

    fetchSupplierDetail(true)
  }, [supplierId])

  const handleDocumentAction = async (
    documentId: string,
    status: 'approved' | 'rejected' | 'observed',
    comment?: string,
    // Sin definir, el back deja la fecha como está; `null` la borra.
    validUntil?: string | null,
  ) => {
    await apiService.request(
      `/internal/suppliers/${supplierId}/documents/${documentId}/review`,
      {
        method: 'POST',
        body: JSON.stringify({
          status,
          reviewComment: comment,
          ...(validUntil !== undefined ? { validUntil } : {}),
        })
      }
    )
    // Refresh data to show updated status
    await fetchSupplierDetail(false)
  }

  const confirmApprove = async () => {
    try {
      setActionLoading(true)
      await apiService.request(
        `/internal/suppliers/${supplierId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: 'approved' })
        }
      )
      setToast({ message: 'Proveedor aprobado exitosamente', type: 'success' })
      setShowApproveModal(false)
      await fetchSupplierDetail(false)
      window.location.reload()
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setToast({ message: err.message || 'Error al aprobar el proveedor', type: 'error' })
    } finally {
      setActionLoading(false)
    }
  }

  const requiredDocs = supplierData?.documents?.filter(d => d.documentType.isRequired) ?? []
  const canApprove = requiredDocs.length > 0 && requiredDocs.every(d => d.latestDocument?.status === 'approved')

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Cargando información del proveedor...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !supplierData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={onBack}
            className={estiloDeBoton('secundario', 'mb-4')}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver</span>
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              <strong>Error:</strong> {error || 'No se pudo cargar la información'}
            </p>
            <button
              onClick={onBack}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Volver al listado
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <SupplierProfileSummary
        title='Expediente del proveedor'
        onBack={onBack}
        onSubmitSuccess={() => { }} // No-op since this is read-only view for admins
        isApproved={supplierData.general.status === 'approved'}
        status={supplierData.general.status}
        profileData={supplierData}
        hideBanner={true}
        onDocumentAction={handleDocumentAction}
        onApprove={() => setShowApproveModal(true)}
        canApprove={canApprove}
        onEdit={onEdit}
        showFinancialDocsRequest={true}
        showBypass={true}
        plazoDelExpediente={supplierData.profileDeadline}
        vistaDelPlazo="admin"
      />

      <ConfirmActionModal
        isOpen={showApproveModal}
        title="Aprobar Proveedor"
        message="¿Estás seguro de que deseas aprobar este proveedor? Esta acción cambiará su estado a 'Aprobado'."
        confirmText="Aprobar"
        variante="exito"
        onConfirm={confirmApprove}
        onCancel={() => setShowApproveModal(false)}
        isLoading={actionLoading}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  )
}

export default SupplierRequestDetail
